/**
 * Backend write-error codes → operator-facing Chinese text.
 *
 * The API client surfaces the backend code both as `errorCode` and, because the managed
 * endpoints answer `{ error, message }` with the code first, as `message`. Without this map
 * the UI alerts raw identifiers ("保存失败: TurbineModelFieldMismatch"), which tells the
 * operator nothing about what to do next.
 *
 * Codes are taken verbatim from the backend; keep them in sync with
 * Services/Tenants/ManagedAssetCatalogWriteService.cs,
 * Services/Tenants/ManagedTurbineWriteService.cs and
 * Services/Tenants/TargetTenantContextResolver.cs.
 */
export const WRITE_ERROR_MESSAGES = {
    // --- target tenant / authority ---
    TenantIdRequired:
        "请求没有携带目标租户，属于前端缺陷。请刷新页面重试；若仍然失败请联系管理员。",
    TenantNotFound: "目标租户不存在或已停用。",
    TenantRouteMismatch: "请求中的租户与所在页面的租户不一致，请刷新页面后重试。",
    TenantContextUnavailable: "该记录没有归属租户，需先修复归属再编辑。",
    PlatformAdminRequired: "当前账号没有平台管理员权限，无法执行该操作。",
    InternalAccountRequired: "该操作仅限内部账号。",
    AuthenticationRequired: "登录状态已失效，请重新登录。",

    // --- field / owner ---
    FieldItemNotFound: "当前风场不存在或已停用，请返回风场列表重新选择。",
    AssetOwnerIdRequired: "缺少资产业主信息。",
    AssetOwnerNotFound: "资产业主不存在。",

    // --- turbine ---
    TurbineIdentityRequired: "请填写风机编号和名称。",
    TurbineRelationRequired: "请选择风机型号，并确认已选定风场。",
    TurbineModelFieldMismatch:
        "所选风机型号已不存在或不可用，请刷新后重新选择。",
    TurbineIdentifierAlreadyExists: "该风机编号在本风场已被占用，请换一个编号。",
    TurbineIdentifierReservedByArchivedRecord:
        "该风机编号被本风场的一条已归档记录占用。请恢复该记录或换一个编号。",
    TurbineArchived: "该风机已归档，需先恢复才能修改。",
    TurbineFieldArchived: "该风机所属风场已归档。",
    TurbineNotFound: "风机不存在。",

    // --- leaf ---
    LeafIdentityRequired: "请填写叶片编号并选择叶片型号。",
    LeafRelationFieldMismatch:
        "所选风机不属于当前风场，或所选叶片型号已不存在。请刷新后重新选择。",
    LeafNotFound: "叶片不存在。",

    // --- catalogue: manufacturers ---
    ManufacturerNameRequired: "请填写厂商名称。",
    TurbineManufacturerNotFound: "风机厂商不存在。",
    LeafManufacturerNotFound: "叶片厂商不存在。",
    TurbineManufacturerInUse: "该风机厂商下仍有型号，无法删除。",
    LeafManufacturerInUse: "该叶片厂商下仍有型号，无法删除。",

    // --- catalogue: models ---
    TurbineModelIdentityRequired: "请填写型号编号并选择厂商。",
    LeafModelIdentityRequired: "请填写型号编号并选择厂商。",
    TurbineManufacturerFieldMismatch: "所选风机厂商已不可用，请刷新后重新选择。",
    LeafManufacturerFieldMismatch: "所选叶片厂商已不可用，请刷新后重新选择。",
    TurbineModelNotFound: "风机型号不存在。",
    LeafModelNotFound: "叶片型号不存在。",
    TurbineModelInUse: "该风机型号已被风机引用，无法删除。",
    LeafModelInUse: "该叶片型号已被叶片引用，无法删除。",
    CatalogFieldMoveRejected: "厂商与型号不能在风场之间移动。",

    // --- routing ---
    ResourceRouteMismatch: "请求地址与提交内容不一致，请刷新页面后重试。",
};

/** Maps a caught API error onto operator-facing text, falling back to the raw message. */
export const describeWriteError = (error) => {
    const code = error?.errorCode || error?.message;
    return WRITE_ERROR_MESSAGES[code] || error?.message || String(error ?? "未知错误");
};
